<template>
    <div>
        <rkm-row>
            <rkm-col>
                <h4>
                    {{ model.cidadao.nome }}
                    <strong v-if="model.cidadao.nomeSocial && model.cidadao.preferenciaNomeId === enums.preferenciaNome.NOME_SOCIAL">
                        ({{ model.cidadao.nomeSocial }})
                    </strong>

                    <span
                        v-if="model.cidadao.responsavelFamiliar"
                        class="ml-1"
                        title="Responsável familiar"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="crown" />
                    </span>

                    <span
                        v-if="isTea"
                        class="ml-1"
                        title="Pessoa com Transtorno do Espectro Autista - TEA"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="infinity" />
                    </span>

                    <span
                        v-if="isPcd"
                        class="ml-1"
                        title="Pessoa com Deficiência - PCD"
                        v-rkm-tooltip.hover
                    >
                        <rkm-icon icon="wheelchair" />
                    </span>
                </h4>
            </rkm-col>
        </rkm-row>

        <rkm-row v-if="model.cidadao.acompanhamentosSaude && model.cidadao.acompanhamentosSaude.length > 0">
            <rkm-col>
                <div class="health-flags">
                    <span class="health-title">
                        Acompanhamentos:
                    </span>

                    <span
                        v-for="(acompanhamento, index) in model.cidadao.acompanhamentosSaude"
                        :key="index"
                        :class="`health-badge acompanhamento-${acompanhamento.value}`"
                    >
                        {{ acompanhamento.text }}
                    </span>
                </div>
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col cols="4">
                <p>
                    <strong>Prontuário:</strong>
                    {{ model.cidadao.prontuario }}
                </p>
                <p>
                    <strong>CPF:</strong>
                    {{ model.cidadao.cpf }}
                </p>
                <p>
                    <strong>CNS:</strong>
                    {{ model.cidadao.cns }}
                </p>
                <p>
                    <strong>RG:</strong>
                    {{ model.cidadao.rg || '-' }}
                </p>
                <p>
                    <strong>Telefone:</strong>
                    <template v-if="model.cidadao.telefoneNumero">
                        {{ model.cidadao.telefoneNumero | formatPhoneOrCellPhone }}
                    </template>
                    <template v-else>
                        -
                    </template>
                    <rkm-button
                        v-if="!isAmbulatorio && verificarPermissao('update-cadastro-paciente')"
                        @click="$refs.ModalTelefone.open(model.cidadao)"
                        class="ml-1"
                        color="primary"
                        title="Alterar telefone"
                        v-rkm-tooltip.hover
                        small
                    >
                        <rkm-icon icon="pen" />
                    </rkm-button>
                </p>
            </rkm-col>

            <rkm-col cols="4">
                <p>
                    <strong>Data de nascimento:</strong>
                    {{ model.cidadao.dataNascimento | formatDate }}
                </p>
                <p>
                    <strong>Idade:</strong>
                    {{ model.cidadao.idade }}
                </p>
                <p>
                    <strong>Sexo:</strong>
                    {{ model.cidadao.sexo || '-' }}
                </p>
                <p>
                    <strong>Nome da mãe:</strong>
                    <template v-if="model.cidadao.nomeMae">
                        {{ model.cidadao.nomeMae }}
                    </template>
                    <template v-else>
                        <i>Desconhecido</i>
                    </template>
                </p>
            </rkm-col>

            <rkm-col cols="4">
                <p>
                    <strong>Classificação:</strong>
                    {{ classificacaoDescricao }}
                </p>
                <p>
                    <strong>Unidade de referência:</strong>
                    <template v-if="model.cidadao.unidadeReferencia">
                        {{ model.cidadao.unidadeReferencia }}
                    </template>
                    <template v-else>
                        <i>Não informado no cadastro do paciente</i>
                    </template>
                </p>
                <p>
                    <strong>Área e Microárea:</strong>
                    {{ model.cidadao.area || 'Não Informada' }} / {{ model.cidadao.microarea || 'Não Informada' }}
                </p>
                <p>
                    <strong>INE:</strong>
                    {{ model.cidadao.ine || 'Não Informado' }}
                </p>
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col>
                <p>
                    <strong>Endereço:</strong>
                    {{ model.cidadao.endereco || 'Não informado' }}
                </p>
            </rkm-col>
        </rkm-row>

        <ModalTelefone ref="ModalTelefone" />
    </div>
</template>

<script>
// Mixins.
import { permissao } from '@/mixins'

// Components.
import ModalTelefone from '@/components/ambulatorio/components/paciente/telefone/Modal'

// Filters.
import filters from '@/filters/formats'

// Enums.
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'
import classificacao from '@/enums/cadastro/paciente/classificacao'
import subclassificacao from '@/enums/cadastro/paciente/subclassificacao'
import deficienciaBase from '@/enums/cadastro/cidadao/deficienciaBase'
import deficiencia from '@/enums/cadastro/cidadao/deficiencia'

export default {
    components: { ModalTelefone },
    computed: {
        classificacaoDescricao() {
            const paciente = this.model.cidadao

            if (paciente.subclassificacaoId == this.enums.subclassificacao.TEMPORARIO) {
                return 'Temporário'
            }

            if (paciente.classificacaoId == this.enums.classificacao.OUTRO_MUNICIPIO) {
                return 'Outro Município'
            }

            return 'Munícipe'
        },
        isTea() {
            const ids = this.model.cidadao.deficienciasBaseIds || []

            return ids.includes(this.enums.deficienciaBase.TEA)
                || this.model.cidadao.deficienciaId === this.enums.deficiencia.AUTISMO
        },
        isPcd() {
            const ids = this.model.cidadao.deficienciasBaseIds || []

            return ids.some(id => id !== this.enums.deficienciaBase.TEA)
                || (!!this.model.cidadao.deficienciaId && this.model.cidadao.deficienciaId !== this.enums.deficiencia.AUTISMO)
        }
    },
    data: () => ({
        enums: {
            preferenciaNome,
            classificacao,
            subclassificacao,
            deficienciaBase,
            deficiencia
        }
    }),
    filters: {
        formatDate: filters.formatDate,
        formatPhoneOrCellPhone: filters.formatPhoneOrCellPhone,
    },
    mixins: [ permissao ],
    props: {
        model: { type: Object, required: false, },
        isAmbulatorio: { type: Boolean, default: false },
    },
}
</script>

<style scoped>
p {
    margin: 0px;
}

.health-flags {
    margin-top: 9px;
    padding-top: 2px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    border-top: 1px solid #e5e7eb;
}

.health-title {
    font-size: 12px;
    color: #6b7280;
    font-weight: 600;
}

.health-badge {
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
}

.acompanhamento-idoso {
    color: #155724;
}

.acompanhamento-gestante {
    color: #ffb6c1;
}

.acompanhamento-hipertenso,
.acompanhamento-diabetico {
    color: #856404;
}

.acompanhamento-saude-mulher {
    color: #b34db2;
}

.acompanhamento-crescimento-desenvolvimento-infantil {
    color: #004085;
}
</style>